if (!customElements.get('community-carousel')) {
  class CommunityCarousel extends HTMLElement {
    connectedCallback() {
      this._waitForGsap();
    }

    _waitForGsap() {
      if (typeof gsap !== 'undefined' && typeof Observer !== 'undefined') {
        this._init();
      } else {
        requestAnimationFrame(() => this._waitForGsap());
      }
    }

    _init() {
      gsap.registerPlugin(Observer);

      this.items = [...this.querySelectorAll('[data-carousel-item]')];
      this.infoSlides = [...this.querySelectorAll('[data-info-slide]')];
      this.prevBtn = this.querySelector('[data-carousel-prev]');
      this.nextBtn = this.querySelector('[data-carousel-next]');
      this.track = this.querySelector('[data-carousel-track]');
      this.viewport = this.querySelector('[data-carousel-viewport]');

      this.count = this.items.length;
      if (this.count === 0) return;

      this.current = 0;
      this.animating = false;
      this.isMuted = true;
      this.isPlaying = true;
      this.loaded = false;
      this.entered = false;

      this._measure();
      this._layout();
      this._bind();
      this._observeSection();
      this._observeHeading();

      window.addEventListener('resize', () => {
        this._measure();
        this._layout();
      });
    }

    _measure() {
      const vw = window.innerWidth;
      this.desktop = vw >= 1024;
      this.gap = 8;

      if (this.desktop) {
        this.activeScale = 1;
        this.inactiveScale = 0.78;
        this.itemW = (vw - 4 * this.gap) / (1 + 3 * this.inactiveScale);
        this.maxVisible = 2;
      } else {
        this.itemW = vw * 0.9;
        this.activeScale = 1;
        this.inactiveScale = 0.85;
        this.maxVisible = 2;
      }

      this.activeH = this.itemW / 0.75;
      this.inactiveH = this.itemW / 0.7;
      this.track.style.height = this.activeH + 'px';
    }

    _slot(itemIndex) {
      let d = itemIndex - this.current;
      const half = this.count / 2;
      if (d > half) d -= this.count;
      if (d < -half) d += this.count;
      return d;
    }

    _x(slot) {
      if (slot === 0) return 0;
      const dir = slot > 0 ? 1 : -1;
      const abs = Math.abs(slot);
      const aW = this.itemW * this.activeScale;
      const iW = this.itemW * this.inactiveScale;
      let x = (aW / 2) + this.gap + (iW / 2);
      if (abs > 1) x += (abs - 1) * (iW + this.gap);
      return x * dir;
    }

    _props(slot) {
      const isActive = slot === 0;
      return {
        x: this._x(slot),
        xPercent: -50,
        y: isActive ? 0 : (this.activeH - this.inactiveH) / 2,
        height: isActive ? this.activeH : this.inactiveH,
        scale: isActive ? this.activeScale : this.inactiveScale,
        borderRadius: this.desktop ? (isActive ? 40 : 24) : 20,
        zIndex: isActive ? 3 : Math.max(0, 3 - Math.abs(slot)),
        opacity: Math.abs(slot) <= this.maxVisible ? 1 : 0,
      };
    }

    _layout() {
      this.items.forEach((item, i) => {
        item.style.width = this.itemW + 'px';
        const props = this._props(this._slot(i));
        if (!this.entered) props.opacity = 0;
        gsap.set(item, props);
      });

      const nav = this.querySelector('[data-carousel-nav]');
      if (nav) nav.style.width = this.itemW + 'px';

      this._syncControls();
      this._syncInfo(false);
    }

    _go(dir) {
      if (this.animating || this.count <= 1 || !this.entered) return;
      this.animating = true;

      const outVideo = this._videoAt(this.current);
      if (outVideo) outVideo.pause();

      const oldSlots = this.items.map((_, i) => this._slot(i));

      this.current = ((this.current + dir) % this.count + this.count) % this.count;

      const newSlots = this.items.map((_, i) => this._slot(i));
      const dur = 0.6;

      this.items.forEach((item, i) => {
        gsap.killTweensOf(item);

        const oldSlot = oldSlots[i];
        const newSlot = newSlots[i];
        const wraps = Math.abs(newSlot - oldSlot) > 1;
        const target = this._props(newSlot);

        if (wraps) {
          if (Math.abs(newSlot) <= this.maxVisible) {
            item.querySelectorAll('img').forEach(img => img.decode().catch(() => {}));
          }

          const tl = gsap.timeline();

          tl.to(item, {
            x: this._x(oldSlot - dir),
            xPercent: -50,
            y: (this.activeH - this.inactiveH) / 2,
            opacity: 0,
            height: this.inactiveH,
            scale: this.inactiveScale,
            borderRadius: this.desktop ? 24 : 20,
            zIndex: 0,
            duration: dur,
            ease: 'power2.inOut',
          });

          tl.set(item, target);
        } else {
          gsap.to(item, {
            ...target,
            duration: dur,
            ease: 'power2.inOut',
          });
        }
      });

      this._syncControls();
      this._syncInfo(true);

      gsap.delayedCall(dur, () => {
        this.animating = false;
        this._playActiveVideo();
      });
    }

    _bind() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this._go(-1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this._go(1));
      }

      if (this.viewport) {
        let startX = 0, startY = 0, axis = null;

        this.viewport.addEventListener('touchstart', (e) => {
          startX = e.changedTouches[0].clientX;
          startY = e.changedTouches[0].clientY;
          axis = null;
        }, { passive: true });

        this.viewport.addEventListener('touchmove', (e) => {
          if (axis) return;
          const dx = Math.abs(e.changedTouches[0].clientX - startX);
          const dy = Math.abs(e.changedTouches[0].clientY - startY);
          if (dx + dy > 30) axis = dx > dy * 2 ? 'x' : 'y';
        }, { passive: true });

        this.viewport.addEventListener('touchend', (e) => {
          if (axis !== 'x') return;
          const dx = e.changedTouches[0].clientX - startX;
          if (Math.abs(dx) > 50) {
            this._go(dx < 0 ? 1 : -1);
          }
        }, { passive: true });
      }

      this.items.forEach((item) => {
        const muteBtn = item.querySelector('[data-mute-toggle]');
        const playBtn = item.querySelector('[data-play-toggle]');

        if (muteBtn) {
          muteBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this._toggleMute();
          });
        }
        if (playBtn) {
          playBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this._togglePlay();
          });
        }
      });
    }

    _observeSection() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !this.loaded) {
            this.loaded = true;
            this._preloadImages();
            this._loadAllVideos();
            observer.disconnect();
          }
        });
      }, { rootMargin: '100% 0px 100% 0px' });

      observer.observe(this);
    }

    _observeHeading() {
      var self = this;
      var heading = this.querySelector('[data-cc-heading]');
      if (!heading) {
        self._animateEntry();
        return;
      }
      var obs = new IntersectionObserver(function (entries) {
        if (entries[0].isIntersecting) {
          obs.disconnect();
          self._animateHeading();
        }
      }, { threshold: 0.5, rootMargin: '0px 0px -10% 0px' });
      obs.observe(heading);
    }

    _animateHeading() {
      var self = this;
      var words = this.querySelectorAll('[data-cc-word]');
      var stagger = 60;
      var wordDuration = 210;
      var totalDuration = (words.length - 1) * stagger + wordDuration;

      words.forEach(function (word, i) {
        var blur = Math.min((i + 1) * 1.5, 10);
        var anim = word.animate(
          [
            { opacity: 0, filter: 'blur(' + blur + 'px)', transform: 'translateY(6px)' },
            { opacity: 1, filter: 'blur(0px)', transform: 'translateY(0)' }
          ],
          { duration: wordDuration, delay: i * stagger, easing: 'ease-out', fill: 'forwards' }
        );
        anim.onfinish = function () {
          word.style.opacity = '1';
          word.style.filter = 'none';
          word.style.transform = 'none';
          anim.cancel();
        };
      });

      setTimeout(function () { self._animateEntry(); }, totalDuration);
    }

    _animateEntry() {
      var self = this;
      var dur = 0.45;

      this.items.forEach(function (item, i) {
        var slot = self._slot(i);
        var absSlot = Math.abs(slot);
        var target = self._props(slot);
        var delay;

        if (absSlot === 0) delay = 0;
        else if (absSlot === 1) delay = 0.195;
        else delay = 0.39;

        if (absSlot > self.maxVisible) {
          gsap.set(item, target);
          return;
        }

        gsap.fromTo(item,
          { y: target.y + 50, opacity: 0 },
          {
            x: target.x,
            xPercent: target.xPercent,
            y: target.y,
            height: target.height,
            scale: target.scale,
            borderRadius: target.borderRadius,
            zIndex: target.zIndex,
            opacity: target.opacity,
            duration: dur,
            delay: delay,
            ease: 'power3.out',
          }
        );
      });

      var nav = self.querySelector('[data-carousel-nav]');
      if (nav) {
        gsap.fromTo(nav,
          { opacity: 0, y: 20 },
          { opacity: 1, y: 0, duration: 0.3, delay: 0.195, ease: 'power2.out' }
        );
      }

      gsap.delayedCall(0.39 + dur, function () {
        self.entered = true;
        self._playActiveVideo();
      });
    }

    _preloadImages() {
      this._preloaded = [];
      this.items.forEach((item) => {
        item.querySelectorAll('img').forEach((img) => {
          img.loading = 'eager';
          const preload = new Image();
          preload.src = img.src;
          this._preloaded.push(preload);
          img.decode().catch(() => {});
        });
      });
    }

    _loadAllVideos() {
      this.items.forEach((item, i) => {
        const video = this._videoAt(i);
        if (!video) return;
        this._loadVideo(video);
        if (i === this.current) {
          video.addEventListener('canplay', () => {
            video.muted = this.isMuted;
            if (this.isPlaying) video.play();
          }, { once: true });
        }
      });
    }

    _videoAt(index) {
      const item = this.items[index];
      if (!item || item.dataset.type !== 'video') return null;
      return item.querySelector('video');
    }

    _loadVideo(video) {
      if (!video) return;
      const sources = video.querySelectorAll('source[data-src]');
      if (sources.length === 0) return;

      sources.forEach((source) => {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
      });
      video.load();
    }

    _playActiveVideo() {
      const video = this._videoAt(this.current);
      if (!video) return;

      const hasSources = video.querySelector('source[src]');
      if (!hasSources) {
        this._loadVideo(video);
        video.addEventListener('canplay', () => {
          video.muted = this.isMuted;
          if (this.isPlaying) video.play();
        }, { once: true });
      } else {
        video.muted = this.isMuted;
        video.currentTime = 0;
        if (this.isPlaying) video.play();
      }
    }

    _toggleMute() {
      this.isMuted = !this.isMuted;
      const video = this._videoAt(this.current);
      if (video) video.muted = this.isMuted;
      this._updateMuteIcons();
    }

    _togglePlay() {
      const video = this._videoAt(this.current);
      if (!video) return;

      if (video.paused) {
        video.play();
        this.isPlaying = true;
      } else {
        video.pause();
        this.isPlaying = false;
      }
      this._updatePlayIcons();
    }

    _syncControls() {
      this.items.forEach((item, i) => {
        const controls = item.querySelector('[data-video-controls]');
        if (controls) {
          controls.style.opacity = i === this.current ? '1' : '0';
          controls.style.pointerEvents = i === this.current ? 'auto' : 'none';
        }
      });
      this._updateMuteIcons();
      this._updatePlayIcons();
    }

    _updateMuteIcons() {
      const activeItem = this.items[this.current];
      if (!activeItem) return;
      const mutedIcon = activeItem.querySelector('[data-icon-muted]');
      const unmutedIcon = activeItem.querySelector('[data-icon-unmuted]');
      if (mutedIcon && unmutedIcon) {
        mutedIcon.style.display = this.isMuted ? 'block' : 'none';
        unmutedIcon.style.display = this.isMuted ? 'none' : 'block';
      }
    }

    _updatePlayIcons() {
      const activeItem = this.items[this.current];
      if (!activeItem) return;
      const pauseIcon = activeItem.querySelector('[data-icon-pause]');
      const playIcon = activeItem.querySelector('[data-icon-play]');
      if (pauseIcon && playIcon) {
        pauseIcon.style.display = this.isPlaying ? 'block' : 'none';
        playIcon.style.display = this.isPlaying ? 'none' : 'block';
      }
    }

    _syncInfo(animate) {
      this.infoSlides.forEach((slide, i) => {
        const isActive = i === this.current;
        const target = isActive ? 1 : 0;

        if (slide._fadeAnim) slide._fadeAnim.cancel();

        if (animate) {
          const from = parseFloat(getComputedStyle(slide).opacity);
          slide._fadeAnim = slide.animate(
            [{ opacity: from }, { opacity: target }],
            { duration: 500, easing: 'ease' }
          );
          slide._fadeAnim.onfinish = () => {
            slide.style.opacity = target;
          };
        } else {
          slide.style.opacity = target;
        }
      });
    }
  }

  customElements.define('community-carousel', CommunityCarousel);
}
